import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EventRegistrationViewProps } from './eventregistrationpage.props';

const EventRegistrationView: React.FC<EventRegistrationViewProps> = ({
  event,
  eventRaceTypes,
  tshirtSizes,
  loading,
  submitting,
  formData,
  handleChange,
  handleSubmit,
  calculatePrice,
  setFormData,
  navigate,
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-10 w-24 mb-6" />
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Настанот не е пронајден</h2>
          <Button onClick={() => navigate('/')}>Врати се на почетна</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Регистрација за {event.title}</CardTitle>
            <p className="text-sm text-gray-600 mt-2">Пополнете ги сите задолжителни полиња</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Лични податоци
                </h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Име *</Label>
                      <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Презиме *</Label>
                      <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Телефон *</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">Град *</Label>
                      <Input id="city" name="city" value={formData.city} onChange={handleChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="country">Држава *</Label>
                      <Input id="country" name="country" value={formData.country} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gender">Пол *</Label>
                      <select
                        id="gender" name="gender" value={formData.gender} onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="Male">Машки</option>
                        <option value="Female">Женски</option>
                        <option value="Other">Друго</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="dateOfBirth">Датум на раѓање *</Label>
                      <Input id="dateOfBirth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required className="mt-1" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Participation Details Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Детали на учество
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="raceTypeId">Тип на трка *</Label>
                    <select
                      id="raceTypeId" name="raceTypeId" value={formData.raceTypeId} onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {eventRaceTypes.length === 0 ? (
                        <option value="">Нема достапни типови на трки</option>
                      ) : (
                        eventRaceTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.name} - {rt.distance}км - {rt.price} МКД
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="participantType">Тип на учесник *</Label>
                      <select
                        id="participantType" name="participantType" value={formData.participantType} onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="Individual">Индивидуален</option>
                        <option value="Team">Тим</option>
                      </select>
                    </div>
                    {formData.participantType === 'Team' && (
                      <div>
                        <Label htmlFor="teamName">Име на тим *</Label>
                        <Input id="teamName" name="teamName" value={formData.teamName} onChange={handleChange} required={formData.participantType === 'Team'} className="mt-1" />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="tshirtSize">Величина на мајчка *</Label>
                    <select
                      id="tshirtSize" name="tshirtSize" value={formData.tshirtSize} onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {tshirtSizes.map((s) => (
                        <option key={s.id} value={s.size}>{s.size}</option>
                      ))}
                    </select>
                  </div>

                  {/* Price Display */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Вкупна цена:</span>
                      <span className="text-2xl font-bold text-blue-600">{calculatePrice()} МКД</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal & Compliance Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Правни и законски услови
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="cookiesConsent"
                      checked={formData.cookiesConsent}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, cookiesConsent: !!checked }))
                      }
                      required
                    />
                    <Label htmlFor="cookiesConsent" className="text-sm font-normal cursor-pointer leading-relaxed">
                      Се согласувам со користење на колачиња (cookies) за подобрување на искуството на веб-страната. *
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="privacyPolicy"
                      checked={formData.privacyPolicy}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, privacyPolicy: !!checked }))
                      }
                      required
                    />
                    <Label htmlFor="privacyPolicy" className="text-sm font-normal cursor-pointer leading-relaxed">
                      Ги прифаќам условите за приватност и се согласувам моите податоци да се чуваат и обработуваат согласно политиката за приватност. *
                    </Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={submitting}>
                  Откажи
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting}>
                  {submitting ? 'Се обработува...' : 'Пријави се'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EventRegistrationView;
